import type { Sponsor } from "@/data/sponsors";

type MetadataSnapshot = {
  tag: HTMLMetaElement;
  created: boolean;
  previousContent?: string;
};

export const DEFAULT_SPONSOR_SOCIAL_IMAGE = "/media/sponsors/social/default.jpg";

export function sponsorSocialImage(sponsor: Sponsor) {
  return sponsor.socialImage || DEFAULT_SPONSOR_SOCIAL_IMAGE;
}

export function applySponsorMetadata(sponsor: Sponsor): () => void {
  const injectedDefaultTitle = document.querySelector<HTMLMetaElement>('meta[name="chi:default-title"]');
  const previousTitle = injectedDefaultTitle?.content ?? document.title;
  document.title = `${sponsor.name} | Sponsor Partner | The CHI Alliance`;
  const socialImage = `${window.location.origin}${sponsorSocialImage(sponsor)}`;

  const updates = [
    { selector: 'meta[name="description"]', attribute: "name", key: "description", content: sponsor.description },
    { selector: 'meta[property="og:title"]', attribute: "property", key: "og:title", content: `${sponsor.name} | The CHI Alliance` },
    { selector: 'meta[property="og:description"]', attribute: "property", key: "og:description", content: sponsor.description },
    { selector: 'meta[property="og:url"]', attribute: "property", key: "og:url", content: `${window.location.origin}/sponsors/${sponsor.slug}` },
    { selector: 'meta[property="og:image"]', attribute: "property", key: "og:image", content: socialImage },
    { selector: 'meta[property="og:image:type"]', attribute: "property", key: "og:image:type", content: "image/jpeg" },
    { selector: 'meta[property="og:image:width"]', attribute: "property", key: "og:image:width", content: "1200" },
    { selector: 'meta[property="og:image:height"]', attribute: "property", key: "og:image:height", content: "630" },
    { selector: 'meta[property="og:image:alt"]', attribute: "property", key: "og:image:alt", content: `${sponsor.name}, a sponsor partner of The CHI Alliance` },
    { selector: 'meta[name="twitter:card"]', attribute: "name", key: "twitter:card", content: "summary_large_image" },
    { selector: 'meta[name="twitter:image"]', attribute: "name", key: "twitter:image", content: socialImage },
    { selector: 'meta[name="twitter:image:alt"]', attribute: "name", key: "twitter:image:alt", content: `${sponsor.name}, a sponsor partner of The CHI Alliance` },
  ].map<MetadataSnapshot>((metadata) => {
    const existing = document.querySelector<HTMLMetaElement>(metadata.selector);
    const tag = existing ?? document.createElement("meta");
    const injected = existing?.hasAttribute("data-sponsor-injected") ?? false;
    const defaultContent = existing?.getAttribute("data-sponsor-default-content");
    const previousContent = defaultContent ?? existing?.content;
    const created = !existing || (injected && defaultContent === null);
    tag.setAttribute(metadata.attribute, metadata.key);
    tag.content = metadata.content;
    if (!existing) document.head.appendChild(tag);
    return { tag, created, previousContent };
  });

  return () => {
    document.title = previousTitle;
    updates.forEach(({ tag, created, previousContent }) => {
      if (created) tag.remove();
      else {
        tag.content = previousContent ?? "";
        tag.removeAttribute("data-sponsor-injected");
        tag.removeAttribute("data-sponsor-default-content");
      }
    });
    injectedDefaultTitle?.remove();
  };
}